<template>
  <div class="Order">
    <div class="shipping-title">
      <PageTitle title="Review Order" />
      <v-alert
        v-if="!activeOrder || !cartItemCount"
        border="start"
        class="ma-4"
        type="info"
      >
        <template #prepend>
          <div class="align-self-start me-4">
            <icon fill="white" height="20" width="20">info-circle</icon>
          </div>
        </template>

        <span>You dont have any items in your cart.</span>

        <span v-if="offer.use_points" class="ms-1">
          To see potential rewards click
          <router-link
            class="text-white"
            :to="{
              name: 'Rewards',
              query: {
                t: $route.query.t
              }
            }"
          >
            here
          </router-link>
          .
        </span>
      </v-alert>

      <order-expiration-timer
        v-if="orderItem"
        :order-product-id="orderItem.id"
      ></order-expiration-timer>
    </div>
    <div v-if="activeOrder" class="order-container">
      <div v-if="cartItemCount">
        <div class="px-4">
          <div class="font-weight-bold my-4 text-subtitle-2">
            Items: {{ cartItemCount }}
          </div>

          <v-divider class="mb-4 mt-0 opacity-1"></v-divider>

          <div>
            <div
              v-for="(type, idx) in ['deals', 'packages', 'products']"
              :key="idx"
            >
              <div
                v-for="(item, idx) in activeOrder[type]"
                :key="idx"
                class="d-flex justify-space-between"
              >
                <div
                  v-for="(itemChild, idx) in item[`order_${type}`]"
                  :key="idx"
                  class="d-flex py-2"
                >
                  <v-img
                    alt="order-media"
                    class="rounded"
                    max-height="63"
                    max-width="115"
                    width="115"
                    height="63"
                    cover
                    :src="getMedia(item)"
                  ></v-img>

                  <div class="d-flex flex-column ml-2 body-2">
                    <div class="pe-2">{{ item.name }}</div>
                    <div>{{ itemChild.name }}</div>
                    <div v-if="itemChild.attribute && itemChild.attribute.name">
                      {{ itemChild.attribute.name }}
                    </div>
                    <span>Qty: 1</span>
                    <span>
                      <v-chip
                        class="my-2"
                        color="blue-lighten-5"
                        size="small"
                        variant="flat"
                      >
                        <div class="text-blue">
                          {{
                            itemChild.delivery_method === 'shipped'
                              ? 'Ship'
                              : 'Pickup'
                          }}
                        </div>
                      </v-chip>
                    </span>
                    <confirm-remove
                      v-model="confirmRemoveModal"
                      @close-dialog="confirmRemoveModal = false"
                      @confirm="confirmRemoveFromOrder"
                    ></confirm-remove>
                    <div>
                        <a
                        class="text-decoration-underline text-black"
                        @click="confirmRemoveModal = true"
                      >
                        <span>Remove</span>
                      </a>
                      <router-link
                        class="text-decoration-underline ml-2 text-black"
                        :to="{
                          name: 'ClaimReward',
                          params: {
                            typeId: item.id,
                            venueId: item.venue_id,
                            type: type.slice(0, -1)
                          },
                          query: {
                            t: $route.query.t
                          }
                        }"
                      >
                        <span>Edit</span>
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="font-weight-bold text-no-wrap order-points">
                  {{ Number(orderItem.points_paid).toLocaleString() }} pts
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-divider class="mx-4 my-5 opacity-1 divider-line-opacity-50"></v-divider>

        <!-- Shipping Details && Pickup Details -->
        <div class="px-5">
          <div v-if="orderItem && orderItem.delivery_method === 'picked_up'">
            <div class="font-weight-bold text-subtitle-2 mb-4 text-medium-emphasis">
              Pickup Details:
            </div>
            <div class="body-2" v-html="orderItem.parent?.pickup_instructions"></div>
          </div>

          <div
            v-if="orderItem.delivery_method === 'shipped'"
            class="d-flex justify-space-between"
          >
            <div class="font-weight-bold text-subtitle-2 mb-4 text-medium-emphasis">
              Shipping Address
            </div>
            <router-link
              class="text-decoration-underline"
              :to="{
                name: 'ShippingInfo',
                params: {
                  typeId: orderItem.parent.id,
                  type: orderItem.parent.type
                },
                query: {
                  t: $route.query.t
                }
              }"
            >
              <span class="body-2 edit-link">Edit</span>
            </router-link>
          </div>

          <div v-if="orderItem.delivery_method === 'shipped'" class="body-2">
            <span v-if="!isAddressValid && orderItem" class="text-red caption">
              You need to finish your shipping info
            </span>
            <template v-else>
              <div>
                {{ customerInfo.first_name }} {{ customerInfo.last_name }}
              </div>
              <div>
                {{ customerInfo.address }}
              </div>
              <div v-if="customerInfo.address_2">
                {{ customerInfo.address_2 }}
              </div>
              <div>
                <span>{{ customerInfo.city }},</span>
                <span class="ml-1">{{ customerInfo.state }}</span>
                <span>&nbsp;{{ customerInfo.zip_code }}</span>
              </div>
            </template>
          </div>
        </div>

        <v-divider class="my-7 opacity-1"></v-divider>

        <div
          v-if="activeOrder.points_paid > 0"
          class="px-4 d-flex justify-space-between text-h6 font-weight-bold"
        >
          <span>Order Points Total</span>
          <span>{{ Number(activeOrder.points_paid).toLocaleString() }}</span>
        </div>

        <v-divider
          v-if="
            activeOrder.points_paid > 0 && activeOrder.price_paid === '0.00'
          "
          class="mx-4 my-3 opacity-1 divider-line-opacity-50"
        ></v-divider>

        <div
          v-if="activeOrder.price_paid === '0.00'"
          class="px-4 d-flex justify-space-between text-h6 font-weight-bold"
        >
          <span>Order Cash Total</span>
          <span>${{ activeOrder.price_paid }}</span>
        </div>

        <v-divider class="my-7 opacity-1"></v-divider>

        <div class="px-4">
          <div class="font-weight-bold text-subtitle-2 mb-4 text-medium-emphasis">
            Payments
          </div>

          <div
            v-if="activeOrder.points_paid > 0"
            class="d-flex justify-space-between text-subtitle-2 font-weight-bold"
          >
            <span class="d-flex align-center">
              <icon height="24" width="24">rewards</icon>
              <span class="ms-2">My Rewards</span>
            </span>
            <span>-{{ Number(activeOrder.points_paid).toLocaleString() }}</span>
          </div>

          <v-divider class="my-3 opacity-1 divider-line-opacity-50"></v-divider>

          <div
            v-if="activeOrder.price_paid === '0.00'"
            class="d-flex justify-space-between text-subtitle-2 font-weight-bold"
          >
            <span class="d-flex align-center">
              <icon height="24" width="24">cash</icon>
              <span class="ms-2">Cash</span>
            </span>
            <span>${{ activeOrder.price_paid }}</span>
          </div>
        </div>

        <v-divider class="my-7 opacity-1 bottom-divider"></v-divider>

        <div class="px-5 order-complete-button-container">
          <span
            v-if="currentPoints < orderItem.points_paid"
            class="red--text caption mb-2 order-complete-error"
          >
            You do not have enough points to claim this.
          </span>
          <v-btn
            v-if="isOrderValid"
            class="font-weight-bold text-decoration-none rounded-pill mb-5 order-complete-btn"
            color="primary"
            variant="flat"
            :disabled="submitting"
            size="large"
            width="100%"
            @click="reviewOrder"
          >
            Complete Order
          </v-btn>
          <v-btn
            v-else-if="!isOrderValid && activeOrder && orderItem"
            class="font-weight-bold text-decoration-none rounded-pill mb-5 order-complete-btn"
            color="primary"
            variant="flat"
            size="large"
            width="100%"
            @click="completeShippingInfo"
          >
            Complete Shipping Info
          </v-btn>
        </div>
      </div>
    </div>

    <OrderComplete v-if="showCompleteOrderModal" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import PageTitle from '@/components/shared/PageTitle/index.vue';
import OrderComplete from '@/components/shared/OrderComplete/index.vue';
import OrderExpirationTimer from '@/components/shared/OrderExpirationTimer/index.vue';
import { clearVuexStorage } from '@/helpers';
import ConfirmRemove from '@/views/Order/components/modals/ConfirmRemove.vue';

export default {
  name: 'Order',
  components: {
    ConfirmRemove,
    PageTitle,
    OrderComplete,
    OrderExpirationTimer
  },
  data() {
    return {
      submitting: false,
      showCompleteOrderModal: false,
      confirmRemoveModal: false
    };
  },
  methods: {
    ...mapActions('cartOrders', [
      'orderExpired',
      'deleteItemFromOrder',
      'orderComplete',
      'resetCart'
    ]),
    ...mapActions('points', ['getPoints']),

    completeShippingInfo() {
      this.$router.push({
        name: 'ShippingInfo',
        params: {
          typeId: this.orderItem.parent.id,
          type: this.orderItem.parent.type
        },
        query: {
          t: this.$route.query.t
        }
      });
    },

    async confirmRemoveFromOrder() {
      // remove this product from the order.
      await this.deleteItemFromOrder({
        orderId: this.activeOrder.id,
        itemType: `order_${this.orderItem.parent.type}s`, // hard coded for products for now.
        itemId: this.orderItem.id
      });

      this.showCompleteOrderModal = false;

      // clear the cache/session in /store/cart/orders
      this.resetCart();

      this.$router.push({
        name: 'Rewards',
        query: {
          t: this.$route.query.t
        }
      });
    },

    getMedia(item) {
      if (item.media && item.media.length > 0) {
        const media = item.media.find(m => m.name === 'Hero');
        if (media) return media.url;
      }
      return 'https://res.cloudinary.com/bandwango/image/upload/v1643751153/noimg_ahhwzj.jpg';
    },
    getCustomerInfo(key) {
      if (key && this.activeOrder) {
        if (this.shipToOther && this.activeOrder?.distribute?.[key])
          return this.activeOrder.distribute[key];
        if (this.activeOrder?.customer?.[key])
          return this.activeOrder.customer[key];

        return this.partnerUser[key];
      }
      return this.partnerUser[key];
    },

    async reviewOrder() {
      this.submitting = true;
      // TODO  if its success then we can show the modal then call the get points
      try {
        const { data } = await this.orderComplete({
          orderId: this.activeOrder.id,
          distribute: this.shipToOther ? this.activeOrder.distribute : {},
          customer: this.shipToOther ? {} : this.activeOrder.customer
        });
        if (data.message) {
          try {
            await this.getPoints({
              partnerUser: this.partnerUser.id,
              params: { offerId: this.offer.id }
            });
          } catch (error) {
            console.error('could not grab the points.', error);
          }
          this.showCompleteOrderModal = true;
          setTimeout(() => {
            clearVuexStorage();
            const query = {};
            const t = this.$route.query.t;
            if (t != null && t !== '') query.t = t;
            const { href } = this.$router.resolve({
              name: 'RewardsHistory',
              query
            });
            window.location.assign(href);
          }, 2000);
        }

        // clear the cache/session in /store/cart/orders for items to be updated on the rewards page
        this.resetCart();
      } catch (error) {
        console.error('Failed to complete your order', error);
      }
      this.submitting = false;
    }
  },
  computed: {
    ...mapState(['offer', 'partnerUser', 'points']),
    ...mapGetters('cartOrders', {
      activeOrder: 'active',
      cartItemCount: 'getCartLength',
      existingOrderItem: 'existingOrderItem'
    }),

    currentPoints() {
      return this.activeOrder.current_points;
    },

    customerInfo() {
      const fields = [
        'address',
        'address_2',
        'city',
        'email',
        'first_name',
        'last_name',
        'phone',
        'state',
        'zip_code'
      ];
      return fields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: this.getCustomerInfo(field)
        }),
        {}
      );
    },

    isAddressValid() {
      if (this.orderItem.delivery_method === 'picked_up') return true;
      if (this.orderItem.delivery_method === 'shipped') {
        // Check if it is distributed first
        const shippedTo =
          this.activeOrder?.distribute || this.activeOrder?.customer;
        return ['address', 'city', 'state', 'country', 'zip_code'].every(
          field => shippedTo?.[field]?.length > 0
        );
      }
      return false;
    },

    isOrderValid() {
      const { first_name: name = false } =
        this.activeOrder?.customer || this.partnerUser;

      return !!this.activeOrder && !!this.orderItem
        ? this.currentPoints >= this.orderItem.points_paid &&
            this.isAddressValid &&
            !!this.cartItemCount &&
            !!name
        : false;
    },

    orderItem() {
      return this.existingOrderItem;
    },

    shipToOther() {
      return (
        !!this.activeOrder?.distribute?.name &&
        !!Object.keys(this.activeOrder.distribute).length > 0
      );
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.v-divider) {
  border-color: rgba(var(--v-theme-on-surface), var(--v-divider-opacity));
}

.divider-line-opacity-50 {
  border-color: rgba(var(--v-theme-on-surface), 0.12);
  opacity: 0.5;
}
.order-container {
  font-size: 0.875rem !important;
  font-weight: 400;
  letter-spacing: 0.0178571429em !important;
  line-height: 1.25rem;
}

:deep(.v-chip) {
  padding: 0px 20px;
}
:deep(.v-chip--size-small) {
  min-height: 24px;
  height: 24px;
}

.order-points {
  color: rgb(var(--v-theme-on-surface));
  font-size: 1rem !important;
  font-weight: 500;
  letter-spacing: 0 !important;
  line-height: 1.5rem;
}

.edit-link {
  color: rgba(var(--v-theme-primary), 1);
  font-size: 0.875rem !important;
  font-weight: 400;
  letter-spacing: 0.0178571429em !important;
  line-height: 1.625rem;
}

.order-complete-btn {
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0em !important;
  min-height: 40px !important;
  height: 40px !important;
  background-color: rgb(var(--v-theme-primary)) !important;
  border-radius: 2rem !important;
  margin-top:1px !important;
}

.order-complete-error {
  color: rgb(var(--v-theme-error)) !important;
}

.bottom-divider {
  margin-bottom: 31px !important;
}
</style>